import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, EventEmitter,
  Input, OnChanges, OnDestroy, OnInit, Output
} from '@angular/core';

import { ClarityConfig } from '../../config/clarity.config';
import { BrightcovePlayer, MediaMetaData, VideoPlayerState } from 'capacitor-brightcove-player';
import { LoggerService } from '../../services/logger.service';
import { getFormattedTimeInMinutesAndSeconds } from '../../utils/format-time';
import { Observable, Subscription } from 'rxjs';
import { getCurrentUserProgram } from '../../store/normalized/selectors/user.selectors';
import { take, withLatestFrom } from 'rxjs/operators';
import { hasEnglishSubtitles } from '../../store/persistent/media/media.selectors';
import { Store } from '@ngrx/store';
import { State } from '../../store';
import { ScreenOrientation } from '@ionic-native/screen-orientation/ngx';
import { PluginListenerHandle } from '@capacitor/core';

@Component({
  selector: 'cl-video-player-brightcove',
  styleUrls: ['video-player-brightcove.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="player-container">
      <div class="player-placeholder" (click)="onPlayVideo()" [ngStyle]="{'background-image': 'url('+ posterUrl +')'}" >
        <img src="assets/imgs/brightcove-play-icon.svg" />
        <div class="progress" [ngStyle]="{width: currentProgress }" ></div>
        <div class="video-length" *ngIf="displayTotalLength">{{displayTotalLength}}</div>
      </div>
    </div>
  `
})
export class VideoPlayerBrightcoveComponent implements OnInit, OnDestroy, OnChanges {
  readonly MINIMUM_PLAYED_SECS = 1;
  @Input() brightcove_key: string;
  @Input() brightcove_poster_url: string = null;

  @Input() controls: Observable<string>;
  @Input() autoplay: boolean;
  @Input() autoSkip: boolean;
  @Input() autoPauseAfter: number;

  @Output() canPlay = new EventEmitter();
  @Output() playerError = new EventEmitter();
  @Output() playedMinimum = new EventEmitter();
  @Output() completed = new EventEmitter();
  @Output() autoSkipped = new EventEmitter();
  @Output() autoPaused = new EventEmitter();
  @Output() closePlayer = new EventEmitter();

  metadata: MediaMetaData = null;
  displayTotalLength: string = null;
  private playedMinimumEmitted = false;
  private progress: string = null;
  private shouldPauseVideo = true;
  private currentMillis = 0;
  private controlsSubscription: Subscription;
  private isVideoPlaying = false;
  private isVideoLoadingOrRunning = false;

  private userLanguage: string;
  private showSubtitles = false;

  private videoListeners: Array<PluginListenerHandle> = [];

  constructor(
    private config: ClarityConfig,
    protected changeDetector: ChangeDetectorRef,
    private loggerService: LoggerService,
    private logger: LoggerService,
    private store: Store<State>,
    private screenOrientation: ScreenOrientation
  ) {}

  get posterUrl(): string {
    if(this.brightcove_poster_url) {
      return this.brightcove_poster_url;
    }
    if(this.metadata?.posterUrl) {
      return this.metadata?.posterUrl;
    }

    return '';
  }

  get currentProgress(): string {
    return this.progress ? this.progress : '0%';
  }

  ngOnChanges(changes) {
    if (changes.brightcove_key) {
      const { brightcove_key } = changes;
      const hasBrightcoveKeyChanges = brightcove_key && brightcove_key.currentValue !== brightcove_key.previousValue && !brightcove_key.firstChange;

      if (!hasBrightcoveKeyChanges) {
        return;
      }

      if (brightcove_key && brightcove_key.currentValue) {
        this.loadVideo();
      }
    }
  }

  ngOnInit() {
    this.store.select(getCurrentUserProgram)
      .pipe(withLatestFrom(this.store.select(hasEnglishSubtitles)), take(1))
      .subscribe(([userProgram, englishSubtitleEnable]) => {

        this.showSubtitles = (userProgram.language_code !== ClarityConfig.DEFAULT_LANGUAGE) ||
          (userProgram.language_code === ClarityConfig.DEFAULT_LANGUAGE && englishSubtitleEnable);

        this.userLanguage = userProgram.language_code;

        this.setListeners();
        this.loadVideo();

        if (this.controls) {
          this.controlsSubscription = this.controls.subscribe((action) => this.parentPlayControl(action));
        }
      });
  }

  loadVideo() {
    this.progress = null;
    this.currentMillis = 0;
    this.playedMinimumEmitted = false;

    BrightcovePlayer.getMetadata({fileId: this.brightcove_key})
      .then((data: { metadata: MediaMetaData }) => {
        this.metadata = data.metadata;
        this.displayTotalLength = getFormattedTimeInMinutesAndSeconds(data.metadata.totalMillis / 1000);
        this.changeDetector.detectChanges();
        this.initializeVideoPlayer();
      })
      .catch(error => {
        this.loggerService.error(error);

        return this.handleError(); // TODO Display a more detailed error message (this is a technical error)
      });
  }

  ngOnDestroy() {
    this.videoListeners.map(listener => listener.remove());
    this.controlsSubscription && this.controlsSubscription.unsubscribe();
  }

  initializeVideoPlayer() {
    if(!this.hasBrightcoveKey()) {
      return;
    }

    if (this.autoplay && !this.autoSkip) {
      this.canPlay.emit(true);
      this.onPlayVideo();
    }

    if (this.autoSkip) {
      this.autoSkipped.emit();
      this.playedMinimum.emit(0);
    }
  }

  async onPlayVideo(): Promise<void> {
    if(!this.hasBrightcoveKey() || this.isVideoLoadingOrRunning) {
      return;
    }
    this.isVideoLoadingOrRunning = true;

    const subtitle = this.showSubtitles ? this.userLanguage : '';

    // Try to run video with non existing subtitles.
    // This is just an error for sentry. This error won't break the app
    if(subtitle && !this.metadata.subtitles.includes(subtitle)) {
      this.logger.error(`Brightcove video player - ${subtitle} subtitle is not available for media : ${this.brightcove_key}`);
    }

    BrightcovePlayer.playVideo({ fileId: this.brightcove_key, local: true, position: this.currentMillis, subtitle })
      .then(() => {

        if (this.config.isAndroid) {
          this.screenOrientation.unlock();
        }

        this.isVideoPlaying = true;
      })
      .catch(error => {
        this.loggerService.error('Error on video playing', error);

        return this.handleError(); // TODO Display a more detailed error message (this is a technical error)
      });
  }

  private hasPlayedMinimum(currentTimer: number): void {
    if(currentTimer > this.MINIMUM_PLAYED_SECS * 1000 && !this.playedMinimumEmitted) {
      this.playedMinimumEmitted = true;
      this.playedMinimum.emit(this.MINIMUM_PLAYED_SECS);
    }
  }

  private async hasCompletedVideo(): Promise<void> {
    if(this.isVideoPlaying) {
      await BrightcovePlayer.closeVideo();
    }
    this.completed.emit();
  }

  private updatePlayerState(playerState: VideoPlayerState): void {
    this.hasPlayedMinimum(playerState.currentMillis);
    this.updateProgress(playerState);
    this.currentMillis = playerState.currentMillis;
    this.changeDetector.detectChanges();
  }

  private async autoPause(playerState: VideoPlayerState): Promise<void> {
    if(this.autoPauseAfter && this.shouldPauseVideo) {
      if(playerState.currentMillis > this.autoPauseAfter * 1000) {
        this.shouldPauseVideo = false;
        await BrightcovePlayer.closeVideo();
        this.autoPaused.emit(this.autoPauseAfter);
      }
    }
  }

  private updateProgress(data: VideoPlayerState): void {
    this.progress = `${Math.round((Number(data.currentMillis) / Number(data.totalMillis)) * 100).toString()}%`;
  }

  private hasBrightcoveKey(): boolean {
    if(this.brightcove_key) {
      return true;
    } else {
      this.logger.error('BrightcovePlayerError', 'Video player requested, but no Brightcove key provided');
      this.handleError(); // TODO Display a more detailed error message (this is a technical error)

      return false;
    }
  }

  private handleError(error = null): void {
    this.playerError.emit(error);
    this.closePlayer.emit();
  }

  private async parentPlayControl(action) {
    switch (action) {
      case 'play':
        return this.resumePlay();
      case 'pause':
        return this.pausePlay();
      default:
        return false;
    }
  }

  private async resumePlay(): Promise<void> {
    if(this.isVideoPlaying) {
      await BrightcovePlayer.playVideo({})
        .then(() => {
          if (this.config.isAndroid) {
            this.screenOrientation.unlock();
          }
        })
        .catch(error => this.loggerService.error('Error on brightcove video resume', error));
    }
  }

  private async pausePlay(): Promise<void> {
    if(this.isVideoPlaying) {
      await BrightcovePlayer.pauseVideo()
        .catch(error => this.loggerService.error('Error on brightcove video pause', error));
    }
  }

  private setListeners(): void {
    this.videoListeners.unshift(BrightcovePlayer.addListener('videoPositionChange', (playerState: VideoPlayerState) => {
      this.updatePlayerState(playerState);
      this.autoPause(playerState);
    }));

    this.videoListeners.unshift(BrightcovePlayer.addListener('closeVideo', (playerState: VideoPlayerState & {completed: boolean}) => {
      this.isVideoPlaying = false;
      this.isVideoLoadingOrRunning = false;
      playerState.completed && this.hasCompletedVideo();
    }));

    this.videoListeners.unshift(BrightcovePlayer.addListener('beforeCloseVideo', () => {
      if (this.config.isAndroid) {
        this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.PORTRAIT);
      }
    }));

    if (this.config.isAndroid) {
      this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.PORTRAIT);
    }
  }
}
